import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Brain, Send, Loader2, CheckCircle, ChevronRight, Trophy, ChevronUp, ChevronDown, ListOrdered } from "lucide-react";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import { IlmekJourney, IlmekStage } from "../../types/ilmek";

const ILMEK_ORDER: IlmekStage[] = ["MERAK", "KESIF", "BAG_KUR", "OGRET", "HATIRLA", "AKTAR", "GOREV"];
const ILMEK_LABEL: Record<IlmekStage, string> = {
  MERAK: "Merak",
  KESIF: "Keşif",
  BAG_KUR: "Bağ Kur",
  OGRET: "Öğret",
  HATIRLA: "Hatırla",
  AKTAR: "Aktar",
  GOREV: "Görev",
};

type ChatMessage = {
  role: "ai" | "student" | "system";
  text: string;
  puan?: number | null;
  eksikler?: string[];
  gorseller?: { url: string; baslik?: string | null }[];
  video?: { url: string; baslik?: string | null } | null;
};

// YouTube URL'inden video kimliğini çıkar (watch?v=, youtu.be/, embed/).
function youtubeId(url: string): string | null {
  const m =
    url.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
    url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
    url.match(/embed\/([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function IlmekSessionPage() {
  const { journeyId } = useParams<{ journeyId: string }>();
  const { showAlert } = useModal();

  const [journey, setJourney] = useState<IlmekJourney | null>(null);
  const [currentIlmek, setCurrentIlmek] = useState<IlmekStage>("MERAK");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [canAdvance, setCanAdvance] = useState(false);
  const [finished, setFinished] = useState(false);
  const [interaktif, setInteraktif] = useState<{
    tip: string;
    baslik?: string | null;
    ogeler?: string[];
    sollar?: string[];
    saglar?: string[];
  } | null>(null);
  const [sira, setSira] = useState<string[]>([]);
  // Eşleştirme: her sol öğe için öğrencinin seçtiği sağ (sol -> sag).
  const [eslestirme, setEslestirme] = useState<Record<string, string>>({});

  const startedRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const openIlmek = useCallback(
    async (ilmek: IlmekStage) => {
      if (!journeyId) return;
      setBusy(true);
      try {
        const res = await ApiService.ilmekTurn(journeyId, ilmek, "");
        const text = [res.ai_yanit, res.sonraki_soru].filter(Boolean).join("\n\n");
        setMessages((m) => [...m, { role: "ai", text, gorseller: res.gorseller, video: res.video }]);
        if (res.interaktif) {
          setInteraktif(res.interaktif);
          if (Array.isArray(res.interaktif.ogeler)) setSira(res.interaktif.ogeler);
          if (res.interaktif.tip === "eslestirme") setEslestirme({});
        }
      } catch (error: any) {
        await showAlert({ title: "Hata", message: error?.message || "AI yanıtı alınamadı.", type: "error" });
      } finally {
        setBusy(false);
      }
    },
    [journeyId, showAlert]
  );

  // Yolculuğu yükle + ilk ilmeği aç
  useEffect(() => {
    if (!journeyId || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const j = await ApiService.getIlmekJourney(journeyId);
        setJourney(j);
        if (j.durum === "TAMAM") {
          setFinished(true);
          return;
        }
        setCurrentIlmek(j.aktifIlmek);
        await openIlmek(j.aktifIlmek);
      } catch (error: any) {
        await showAlert({ title: "Hata", message: error?.message || "Oturum yüklenemedi.", type: "error" });
      }
    })();
  }, [journeyId, openIlmek, showAlert]);

  const send = useCallback(async () => {
    const girdi = input.trim();
    if (!girdi || !journeyId || busy) return;
    setMessages((m) => [...m, { role: "student", text: girdi }]);
    setInput("");
    setBusy(true);
    try {
      const res = await ApiService.ilmekTurn(journeyId, currentIlmek, girdi);
      const text = [res.ai_yanit, res.sonraki_soru].filter(Boolean).join("\n\n");
      setMessages((m) => [...m, { role: "ai", text, puan: res.puan, eksikler: res.eksikler, gorseller: res.gorseller, video: res.video }]);
      if (res.esik_gecti) setCanAdvance(true);
    } catch (error: any) {
      await showAlert({ title: "Hata", message: error?.message || "AI yanıtı alınamadı.", type: "error" });
    } finally {
      setBusy(false);
    }
  }, [input, journeyId, currentIlmek, busy, showAlert]);

  const advance = useCallback(async () => {
    if (!journeyId || busy) return;
    setBusy(true);
    setCanAdvance(false);
    try {
      const j = await ApiService.advanceIlmekJourney(journeyId);
      setJourney(j);
      if (j.durum === "TAMAM") {
        setMessages((m) => [...m, { role: "system", text: "🎉 Tebrikler! Bu kazanımın tüm ilmeklerini tamamladın." }]);
        setFinished(true);
        setBusy(false);
        return;
      }
      setCurrentIlmek(j.aktifIlmek);
      setMessages((m) => [...m, { role: "system", text: `Sıradaki ilmek: ${ILMEK_LABEL[j.aktifIlmek]}` }]);
      await openIlmek(j.aktifIlmek);
    } catch (error: any) {
      await showAlert({ title: "Hata", message: error?.message || "Sonraki ilmeğe geçilemedi.", type: "error" });
      setBusy(false);
    }
  }, [journeyId, busy, openIlmek, showAlert]);

  const move = useCallback((idx: number, dir: number) => {
    setSira((s) => {
      const a = s.slice();
      const j = idx + dir;
      if (j < 0 || j >= a.length) return s;
      [a[idx], a[j]] = [a[j], a[idx]];
      return a;
    });
  }, []);

  const submitSira = useCallback(async () => {
    if (!journeyId || busy || !interaktif) return;
    setMessages((m) => [...m, { role: "student", text: sira.join("  →  ") }]);
    setBusy(true);
    try {
      const res = await ApiService.ilmekTurn(journeyId, "GOREV", JSON.stringify(sira));
      const text = [res.ai_yanit, res.sonraki_soru].filter(Boolean).join("\n\n");
      setMessages((m) => [...m, { role: "ai", text, puan: res.puan, eksikler: res.eksikler }]);
      if (res.esik_gecti) {
        setInteraktif(null);
        setCanAdvance(true);
      }
    } catch (error: any) {
      await showAlert({ title: "Hata", message: error?.message || "Görev kontrol edilemedi.", type: "error" });
    } finally {
      setBusy(false);
    }
  }, [journeyId, busy, interaktif, sira, showAlert]);

  const submitEslestirme = useCallback(async () => {
    if (!journeyId || busy || !interaktif || !interaktif.sollar) return;
    const sollar = interaktif.sollar;
    if (sollar.some((s) => !eslestirme[s])) {
      await showAlert({ title: "Eksik", message: "Lütfen her öğe için bir eşleşme seç.", type: "warning" });
      return;
    }
    const ciftler = sollar.map((s) => [s, eslestirme[s]]);
    setMessages((m) => [
      ...m,
      { role: "student", text: ciftler.map(([s, d]) => `${s} → ${d}`).join("  •  ") },
    ]);
    setBusy(true);
    try {
      const res = await ApiService.ilmekTurn(journeyId, "GOREV", JSON.stringify(ciftler));
      const text = [res.ai_yanit, res.sonraki_soru].filter(Boolean).join("\n\n");
      setMessages((m) => [...m, { role: "ai", text, puan: res.puan, eksikler: res.eksikler }]);
      if (res.esik_gecti) {
        setInteraktif(null);
        setCanAdvance(true);
      }
    } catch (error: any) {
      await showAlert({ title: "Hata", message: error?.message || "Görev kontrol edilemedi.", type: "error" });
    } finally {
      setBusy(false);
    }
  }, [journeyId, busy, interaktif, eslestirme, showAlert]);

  const currentIdx = ILMEK_ORDER.indexOf(currentIlmek);

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 80px)" }}>
      {/* Başlık + ilerleme */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between gap-3">
          <Link to="/ilmek/ogren" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4" /> Dersler
          </Link>
          <span className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
            {journey?.kazanimKodu || ""}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {ILMEK_ORDER.map((ilmek, idx) => {
            const done = finished || idx < currentIdx;
            const active = !finished && idx === currentIdx;
            return (
              <span
                key={ilmek}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${
                  active
                    ? "bg-purple-600 text-white"
                    : done
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {done && <CheckCircle className="w-3 h-3" />}
                {ILMEK_LABEL[ilmek]}
              </span>
            );
          })}
        </div>
      </div>

      {/* Sohbet */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-4 overflow-y-auto space-y-3 mb-3">
        {messages.map((msg, i) => {
          if (msg.role === "system") {
            return (
              <div key={i} className="text-center text-xs text-gray-400 py-1">
                — {msg.text} —
              </div>
            );
          }
          const isAi = msg.role === "ai";
          return (
            <div key={i} className={`flex ${isAi ? "justify-start" : "justify-end"}`}>
              <div className={`flex items-start gap-2 max-w-[85%] ${isAi ? "" : "flex-row-reverse"}`}>
                {isAi && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Brain className="w-4 h-4 text-purple-600" />
                  </div>
                )}
                <div
                  className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                    isAi ? "bg-gray-50 text-gray-800 border border-gray-100" : "bg-purple-600 text-white"
                  }`}
                >
                  {msg.text}
                  {isAi && msg.gorseller && msg.gorseller.length > 0 && (
                    <div className="mt-2 grid grid-cols-1 gap-2">
                      {msg.gorseller.map((g, gi) => (
                        <figure key={gi} className="rounded-lg overflow-hidden border border-gray-200 bg-white">
                          <img src={g.url} alt={g.baslik || "görsel"} className="w-full h-auto block" loading="lazy" />
                          {g.baslik && (
                            <figcaption className="text-[11px] text-gray-500 px-2 py-1 border-t border-gray-100">{g.baslik}</figcaption>
                          )}
                        </figure>
                      ))}
                    </div>
                  )}
                  {isAi && msg.video && msg.video.url && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-200 bg-black">
                      {youtubeId(msg.video.url) ? (
                        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                          <iframe
                            className="absolute inset-0 w-full h-full"
                            src={`https://www.youtube-nocookie.com/embed/${youtubeId(msg.video.url)}`}
                            title={msg.video.baslik || "Konu videosu"}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <a
                          href={msg.video.url}
                          target="_blank"
                          rel="noreferrer"
                          className="block text-center text-sm text-white py-3 hover:underline"
                        >
                          ▶ {msg.video.baslik || "Videoyu aç"}
                        </a>
                      )}
                    </div>
                  )}
                  {isAi && typeof msg.puan === "number" && (
                    <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                      Puan: <span className="font-semibold text-gray-700">{Math.round(msg.puan * 100)} / 100</span>
                      {msg.eksikler && msg.eksikler.length > 0 && (
                        <span className="block mt-1">Eksik: {msg.eksikler.join(", ")}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {busy && (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> öğretmen düşünüyor...
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Alt: girdi / ilerle / bitti */}
      {finished ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
          <Trophy className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <div className="font-semibold text-emerald-800">Yolculuk tamamlandı!</div>
          <Link to="/ilmek/ogren" className="inline-block mt-3 text-sm text-purple-700 hover:underline">
            Başka bir kazanıma geç →
          </Link>
        </div>
      ) : interaktif && interaktif.tip === "eslestirme" ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="text-sm font-medium text-gray-700 mb-3 inline-flex items-center gap-1.5">
            <ListOrdered className="w-4 h-4 text-purple-600" />
            {interaktif.baslik || "Eşleştir"} — her öğeyi doğru karşılığıyla eşleştir
          </div>
          <div className="space-y-2">
            {(interaktif.sollar || []).map((sol) => (
              <div key={sol} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                <span className="flex-1 text-sm font-medium text-gray-800">{sol}</span>
                <span className="text-gray-400">→</span>
                <select
                  value={eslestirme[sol] || ""}
                  onChange={(e) => setEslestirme((p) => ({ ...p, [sol]: e.target.value }))}
                  disabled={busy}
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 disabled:opacity-50"
                >
                  <option value="">Seç…</option>
                  {(interaktif.saglar || []).map((sag) => (
                    <option key={sag} value={sag}>
                      {sag}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-3">
            <button
              onClick={submitEslestirme}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" /> Kontrol Et
            </button>
          </div>
        </div>
      ) : interaktif ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="text-sm font-medium text-gray-700 mb-3 inline-flex items-center gap-1.5">
            <ListOrdered className="w-4 h-4 text-purple-600" />
            {interaktif.baslik || "Sırala"} — öğeleri doğru sıraya diz
          </div>
          <div className="space-y-2">
            {sira.map((o, i) => (
              <div key={o} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-semibold flex-shrink-0">
                  {i + 1}
                </span>
                <span className="flex-1 text-sm text-gray-800">{o}</span>
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0 || busy}
                  className="p-1 rounded text-gray-500 hover:bg-gray-200 disabled:opacity-30"
                  aria-label="Yukarı"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === sira.length - 1 || busy}
                  className="p-1 rounded text-gray-500 hover:bg-gray-200 disabled:opacity-30"
                  aria-label="Aşağı"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-3">
            <button
              onClick={submitSira}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" /> Kontrol Et
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {canAdvance && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <span className="text-sm text-emerald-700 inline-flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> Bu ilmeği geçtin! İstersen sohbete devam et ya da ilerle.
              </span>
              <button
                onClick={advance}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex-shrink-0"
              >
                Sonraki ilmeğe geç <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Cevabını yaz..."
              disabled={busy}
              className="flex-1 resize-none px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 disabled:opacity-50"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> Gönder
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
